import React from 'react';

/**
 * Portal Page Styles
 *
 * Styles for the marketing/landing page at checkademic.com
 *
 * Usage Philosophy:
 * - Modern, bold design with gradients and visual impact
 * - Responsive patterns for desktop and mobile
 * - Pre-composed sections for consistent marketing page structure
 *
 * Brand Colors:
 * - Navy: #00233c (primary)
 * - Orange: #f38d4b (accent)
 */

// ============================================================================
// PAGE LAYOUT
// ============================================================================

export const portalPageStyles = {
  /**
   * Page Container - Full page wrapper
   */
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  } as React.CSSProperties,

  /**
   * Content Container - Max-width wrapper
   */
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
  } as React.CSSProperties,

  /**
   * Section - Standard section spacing
   */
  section: {
    padding: '2.5rem 0',
  } as React.CSSProperties,

  /**
   * Section Title - Centered heading for each section
   */
  sectionTitle: {
    fontSize: '2rem',
    fontWeight: 700,
    color: 'var(--text-primary)',
    marginBottom: '2rem',
    textAlign: 'center' as const,
  } as React.CSSProperties,
};

// ============================================================================
// NAVIGATION STYLES
// ============================================================================

export const portalNavStyles = {
  nav: {
    backgroundColor: '#00233c',
    color: 'white',
    position: 'sticky' as const,
    top: 0,
    zIndex: 100,
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    padding: '0 2rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  } as React.CSSProperties,

  navMobile: {
    backgroundColor: '#00233c',
    color: 'white',
    position: 'sticky' as const,
    top: 0,
    zIndex: 100,
    height: '56px',
    display: 'flex',
    alignItems: 'center',
    padding: '0 1rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  } as React.CSSProperties,

  content: {
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  } as React.CSSProperties,

  links: {
    display: 'flex',
    alignItems: 'center',
    gap: '1.5rem',
  } as React.CSSProperties,

  linksMobile: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  } as React.CSSProperties,

  link: {
    color: 'rgba(255,255,255,0.8)',
    textDecoration: 'none',
    fontSize: '0.95rem',
    fontWeight: 500,
    padding: '0.25rem 0',
    borderBottom: '2px solid transparent',
    transition: 'color 0.2s, border-color 0.2s',
  } as React.CSSProperties,

  linkMobile: {
    color: 'rgba(255,255,255,0.8)',
    textDecoration: 'none',
    fontSize: '0.85rem',
    fontWeight: 500,
    padding: '0.25rem 0',
    borderBottom: '2px solid transparent',
    transition: 'color 0.2s, border-color 0.2s',
  } as React.CSSProperties,

  linkActive: {
    color: 'white',
    borderBottom: '2px solid #f38d4b',
  } as React.CSSProperties,
};

// ============================================================================
// HERO SECTION
// ============================================================================

export const portalHeroStyles = {
  /**
   * Hero Container - Full-width gradient background
   */
  container: {
    background: 'linear-gradient(135deg, #00233c 0%, #003a5c 100%)',
    color: 'white',
    padding: '4rem 2rem',
    position: 'relative' as const,
    overflow: 'hidden' as const,
  } as React.CSSProperties,

  /**
   * Hero Content - Two column layout wrapper
   */
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '3rem',
    alignItems: 'center',
  } as React.CSSProperties,

  /**
   * Hero Content Mobile - Single column
   */
  contentMobile: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2rem',
    textAlign: 'center' as const,
  } as React.CSSProperties,

  /**
   * Hero Text Column
   */
  textColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1.5rem',
  } as React.CSSProperties,

  /**
   * Hero Headline - Main heading
   */
  headline: {
    fontSize: '3rem',
    fontWeight: 700,
    lineHeight: 1.1,
    margin: 0,
  } as React.CSSProperties,

  /**
   * Hero Headline Mobile
   */
  headlineMobile: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1.2,
    margin: 0,
  } as React.CSSProperties,

  /**
   * Hero Subheadline - Supporting text
   */
  subheadline: {
    fontSize: '1.25rem',
    opacity: 0.9,
    lineHeight: 1.6,
    margin: 0,
  } as React.CSSProperties,

  /**
   * Hero Subheadline Mobile
   */
  subheadlineMobile: {
    fontSize: '1rem',
    opacity: 0.9,
    lineHeight: 1.6,
    margin: 0,
  } as React.CSSProperties,

  /**
   * Hero CTA Button
   */
  ctaButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '1rem 2rem',
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#00233c',
    backgroundColor: '#f38d4b',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    alignSelf: 'flex-start',
  } as React.CSSProperties,

  /**
   * Hero CTA Button Mobile - Centered
   */
  ctaButtonMobile: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '1rem 2rem',
    fontSize: '1rem',
    fontWeight: 600,
    color: '#00233c',
    backgroundColor: '#f38d4b',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    width: '100%',
  } as React.CSSProperties,

  /**
   * Hero Graphic Column
   */
  graphicColumn: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  } as React.CSSProperties,

  /**
   * Hero Image - Photo with tilt and shadow
   */
  heroImage: {
    borderRadius: '12px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
    transform: 'rotate(1.5deg)',
    backfaceVisibility: 'hidden' as const,
    border: '4px solid rgba(255,255,255,0.15)',
    objectFit: 'cover' as const,
    maxWidth: '480px',
    width: '100%',
    height: 'auto',
  } as React.CSSProperties,
};

// ============================================================================
// VALUE PROPOSITION STRIP
// ============================================================================

export const portalValuePropStyles = {
  /**
   * Strip Container - Background strip
   */
  container: {
    backgroundColor: 'var(--surface-color)',
    borderBottom: '1px solid var(--border-color)',
    padding: '2rem',
  } as React.CSSProperties,

  /**
   * Strip Content - Grid of value boxes
   */
  content: {
    maxWidth: '900px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '2rem',
    textAlign: 'center' as const,
  } as React.CSSProperties,

  /**
   * Strip Content Mobile - Stacked
   */
  contentMobile: {
    maxWidth: '400px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1.5rem',
    textAlign: 'center' as const,
  } as React.CSSProperties,

  /**
   * Value Box
   */
  box: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  } as React.CSSProperties,

  /**
   * Value Number - Large metric
   */
  number: {
    fontSize: '2.5rem',
    fontWeight: 700,
    color: 'var(--primary-color)',
    lineHeight: 1,
  } as React.CSSProperties,

  /**
   * Value Label - Description
   */
  label: {
    fontSize: '0.875rem',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  } as React.CSSProperties,
};

// ============================================================================
// FEATURES SECTION
// ============================================================================

export const portalFeatureStyles = {
  /**
   * Feature Grid - 2x2 layout
   */
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '1.5rem',
  } as React.CSSProperties,

  /**
   * Feature Grid Mobile - Single column
   */
  gridMobile: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
  } as React.CSSProperties,

  /**
   * Feature Card
   */
  card: {
    backgroundColor: 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    borderRadius: '12px',
    padding: '2rem',
    transition: 'all 0.3s ease',
  } as React.CSSProperties,

  /**
   * Feature Card Hover State
   */
  cardHover: {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
  } as React.CSSProperties,

  /**
   * Feature Icon Container
   */
  iconContainer: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: 'rgba(243, 141, 75, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '1rem',
  } as React.CSSProperties,

  /**
   * Feature Icon
   */
  icon: {
    color: '#f38d4b',
  } as React.CSSProperties,

  /**
   * Feature Title
   */
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: '0.5rem',
  } as React.CSSProperties,

  /**
   * Feature Description
   */
  description: {
    fontSize: '0.95rem',
    color: 'var(--text-secondary)',
    lineHeight: 1.6,
    margin: 0,
  } as React.CSSProperties,
};

// ============================================================================
// HOW IT WORKS SECTION
// ============================================================================

export const portalStepsStyles = {
  /**
   * Steps Container - Horizontal flow
   */
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: '1rem',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,

  /**
   * Steps Container Mobile - Vertical flow
   */
  containerMobile: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1.5rem',
    alignItems: 'center',
  } as React.CSSProperties,

  /**
   * Step Item
   */
  item: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '0.75rem',
    textAlign: 'center' as const,
    maxWidth: '200px',
  } as React.CSSProperties,

  /**
   * Step Number Circle
   */
  number: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.5rem',
    fontWeight: 700,
  } as React.CSSProperties,

  /**
   * Step Icon - Inside circle
   */
  icon: {
    color: 'white',
  } as React.CSSProperties,

  /**
   * Step Title
   */
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: 0,
  } as React.CSSProperties,

  /**
   * Step Description
   */
  description: {
    fontSize: '0.875rem',
    color: 'var(--text-secondary)',
    margin: 0,
  } as React.CSSProperties,

  /**
   * Arrow Connector
   */
  arrow: {
    display: 'flex',
    alignItems: 'center',
    color: 'var(--border-color)',
    paddingTop: '1rem',
  } as React.CSSProperties,
};

// ============================================================================
// DUAL AUDIENCE SECTION
// ============================================================================

export const portalAudienceStyles = {
  /**
   * Audience Grid - Two columns
   */
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '2rem',
  } as React.CSSProperties,

  /**
   * Audience Grid Mobile - Stacked
   */
  gridMobile: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1.5rem',
  } as React.CSSProperties,

  /**
   * Audience Card
   */
  card: {
    backgroundColor: 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    borderRadius: '12px',
    padding: '2rem',
  } as React.CSSProperties,

  /**
   * Audience Card Header
   */
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '1.5rem',
  } as React.CSSProperties,

  /**
   * Audience Icon
   */
  icon: {
    color: 'var(--primary-color)',
  } as React.CSSProperties,

  /**
   * Audience Title
   */
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: 0,
  } as React.CSSProperties,

  /**
   * Audience List
   */
  list: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
  } as React.CSSProperties,

  /**
   * Audience List Item
   */
  listItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    fontSize: '0.95rem',
    color: 'var(--text-secondary)',
    lineHeight: 1.5,
  } as React.CSSProperties,

  /**
   * List Item Check Icon
   */
  checkIcon: {
    color: '#22c55e',
    flexShrink: 0,
    marginTop: '2px',
  } as React.CSSProperties,
};

// ============================================================================
// FAQ SECTION
// ============================================================================

export const portalFaqStyles = {
  /**
   * FAQ Container
   */
  container: {
    maxWidth: '800px',
    margin: '0 auto',
  } as React.CSSProperties,

  /**
   * FAQ Item
   */
  item: {
    backgroundColor: 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    padding: '1.5rem',
    marginBottom: '1rem',
  } as React.CSSProperties,

  /**
   * FAQ Question
   */
  question: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: '0.75rem',
  } as React.CSSProperties,

  /**
   * FAQ Answer
   */
  answer: {
    fontSize: '0.95rem',
    color: 'var(--text-secondary)',
    lineHeight: 1.6,
    margin: 0,
  } as React.CSSProperties,
};

// ============================================================================
// TESTIMONIALS SECTION
// ============================================================================

export const portalTestimonialStyles = {
  /**
   * Testimonial Grid - Three columns
   */
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '1.5rem',
  } as React.CSSProperties,

  /**
   * Testimonial Grid Mobile - Stacked
   */
  gridMobile: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
  } as React.CSSProperties,

  /**
   * Testimonial Card
   */
  card: {
    backgroundColor: 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    borderRadius: '12px',
    padding: '1.5rem',
  } as React.CSSProperties,

  /**
   * Quote Mark
   */
  quoteMark: {
    fontSize: '3rem',
    lineHeight: 1,
    color: 'var(--border-color)',
    marginBottom: '0.5rem',
  } as React.CSSProperties,

  /**
   * Quote Text
   */
  quote: {
    fontSize: '0.95rem',
    color: 'var(--text-primary)',
    lineHeight: 1.6,
    fontStyle: 'italic',
    marginBottom: '1rem',
  } as React.CSSProperties,

  /**
   * Attribution
   */
  attribution: {
    fontSize: '0.875rem',
    color: 'var(--text-secondary)',
  } as React.CSSProperties,

  /**
   * Attribution Role
   */
  role: {
    fontSize: '0.75rem',
    color: 'var(--text-secondary)',
    opacity: 0.8,
  } as React.CSSProperties,

  /**
   * Example Badge - For placeholder testimonials
   */
  exampleBadge: {
    display: 'inline-block',
    fontSize: '0.625rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
    color: 'var(--text-secondary)',
    backgroundColor: 'var(--background-color)',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    marginBottom: '0.5rem',
  } as React.CSSProperties,
};

// ============================================================================
// PRICING SECTION
// ============================================================================

export const portalPricingStyles = {
  /**
   * Pricing Container
   */
  container: {
    backgroundColor: 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    borderRadius: '16px',
    padding: '3rem',
    textAlign: 'center' as const,
    maxWidth: '600px',
    margin: '0 auto',
  } as React.CSSProperties,

  /**
   * Pricing Container Mobile
   */
  containerMobile: {
    backgroundColor: 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    borderRadius: '12px',
    padding: '2rem 1.5rem',
    textAlign: 'center' as const,
  } as React.CSSProperties,

  /**
   * Pricing Title
   */
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: '0.75rem',
  } as React.CSSProperties,

  /**
   * Pricing Description
   */
  description: {
    fontSize: '1rem',
    color: 'var(--text-secondary)',
    marginBottom: '1.5rem',
    lineHeight: 1.6,
  } as React.CSSProperties,

  /**
   * Pricing CTA Button
   */
  ctaButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.875rem 2rem',
    fontSize: '1rem',
    fontWeight: 600,
    color: 'white',
    backgroundColor: 'var(--primary-color)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textDecoration: 'none',
  } as React.CSSProperties,

  /**
   * Pricing Secondary Text
   */
  secondaryText: {
    fontSize: '0.875rem',
    color: 'var(--text-secondary)',
    marginTop: '1rem',
  } as React.CSSProperties,
};

// ============================================================================
// SCHOOL FINDER SECTION
// ============================================================================

export const portalFinderStyles = {
  /**
   * Finder Container
   */
  container: {
    backgroundColor: 'var(--surface-color)',
    padding: '3rem 2rem',
    borderRadius: '12px',
    border: '1px solid var(--border-color)',
  } as React.CSSProperties,

  /**
   * Finder Container Mobile
   */
  containerMobile: {
    backgroundColor: 'var(--surface-color)',
    padding: '2rem 1rem',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
  } as React.CSSProperties,

  /**
   * Search Box Container
   */
  searchBox: {
    display: 'flex',
    gap: '1rem',
    maxWidth: '600px',
    margin: '0 auto 2rem',
  } as React.CSSProperties,

  /**
   * Search Box Mobile
   */
  searchBoxMobile: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
    margin: '0 auto 2rem',
  } as React.CSSProperties,

  /**
   * Search Input
   */
  input: {
    flex: 1,
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    border: '2px solid var(--border-color)',
    borderRadius: '6px',
    outline: 'none',
  } as React.CSSProperties,

  /**
   * Search Input Mobile
   */
  inputMobile: {
    padding: '0.875rem 1rem',
    fontSize: '1rem',
    border: '2px solid var(--border-color)',
    borderRadius: '6px',
    outline: 'none',
    minHeight: '48px',
  } as React.CSSProperties,

  /**
   * Search Button
   */
  button: {
    padding: '0.75rem 2rem',
    fontSize: '1rem',
    fontWeight: 600,
    color: 'white',
    backgroundColor: 'var(--primary-color)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  } as React.CSSProperties,

  /**
   * Search Button Mobile
   */
  buttonMobile: {
    padding: '0.875rem 1.5rem',
    fontSize: '1rem',
    fontWeight: 600,
    color: 'white',
    backgroundColor: 'var(--primary-color)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    width: '100%',
    minHeight: '48px',
  } as React.CSSProperties,

  /**
   * Results Grid
   */
  resultsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '1rem',
    maxWidth: '900px',
    margin: '0 auto',
  } as React.CSSProperties,

  /**
   * Results Grid Mobile
   */
  resultsGridMobile: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
  } as React.CSSProperties,

  /**
   * School Card
   */
  schoolCard: {
    backgroundColor: 'var(--background-color)',
    padding: '1.25rem',
    borderRadius: '8px',
    border: '2px solid var(--border-color)',
    cursor: 'pointer',
    transition: 'all 0.2s',
  } as React.CSSProperties,

  /**
   * School Card Hover
   */
  schoolCardHover: {
    borderColor: 'var(--primary-color)',
    backgroundColor: 'var(--surface-color)',
  } as React.CSSProperties,

  /**
   * School Name
   */
  schoolName: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: '0.25rem',
  } as React.CSSProperties,

  /**
   * School Subdomain
   */
  schoolSubdomain: {
    fontSize: '0.875rem',
    color: 'var(--text-secondary)',
  } as React.CSSProperties,

  /**
   * School Contact Email
   */
  schoolContact: {
    fontSize: '0.8rem',
    color: 'var(--primary-color)',
    marginTop: '0.5rem',
    display: 'block',
    textDecoration: 'none',
  } as React.CSSProperties,

  /**
   * Error Message
   */
  error: {
    color: 'var(--error-color)',
    padding: '0.5rem',
    textAlign: 'center' as const,
    marginBottom: '1rem',
  } as React.CSSProperties,
};

// ============================================================================
// FOOTER SECTION
// ============================================================================

export const portalFooterStyles = {
  /**
   * Footer Container
   */
  container: {
    backgroundColor: 'var(--surface-color)',
    padding: '2rem',
    textAlign: 'center' as const,
    borderTop: '1px solid var(--border-color)',
    marginTop: '2.5rem',
  } as React.CSSProperties,

  /**
   * Footer Text
   */
  text: {
    fontSize: '0.95rem',
    color: 'var(--text-secondary)',
    marginBottom: '0.5rem',
  } as React.CSSProperties,

  /**
   * Footer Link
   */
  link: {
    color: 'inherit',
    textDecoration: 'none',
  } as React.CSSProperties,

  /**
   * Current Page Text - Non-clickable, dimmed link for current page
   */
  currentPageText: {
    color: 'inherit',
    opacity: 0.6,
    cursor: 'default',
  } as React.CSSProperties,
};

// ============================================================================
// PRICING PAGE STYLES
// ============================================================================

export const portalPricingPageStyles = {
  /**
   * Hero Section - Navy gradient banner
   */
  hero: {
    background: 'linear-gradient(135deg, #00233c 0%, #003a5c 100%)',
    color: 'white',
    padding: '3.5rem 2rem',
    textAlign: 'center' as const,
  } as React.CSSProperties,

  heroTitle: {
    fontSize: '2.5rem',
    fontWeight: 700,
    margin: '0 0 0.75rem',
  } as React.CSSProperties,

  heroTitleMobile: {
    fontSize: '1.75rem',
    fontWeight: 700,
    margin: '0 0 0.75rem',
  } as React.CSSProperties,

  heroSubtitle: {
    fontSize: '1.15rem',
    opacity: 0.9,
    margin: 0,
    lineHeight: 1.6,
  } as React.CSSProperties,

  heroSubtitleMobile: {
    fontSize: '1rem',
    opacity: 0.9,
    margin: 0,
    lineHeight: 1.6,
  } as React.CSSProperties,

  /**
   * Desktop Table Styles
   */
  tableWrapper: {
    overflowX: 'auto' as const,
    margin: '0 auto',
    maxWidth: '960px',
  } as React.CSSProperties,

  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.95rem',
  } as React.CSSProperties,

  caption: {
    fontSize: '0.85rem',
    color: 'var(--text-secondary)',
    marginBottom: '0.75rem',
    textAlign: 'left' as const,
    captionSide: 'top' as const,
  } as React.CSSProperties,

  th: {
    padding: '0.75rem 1rem',
    textAlign: 'center' as const,
    fontWeight: 600,
    color: 'white',
    backgroundColor: '#00233c',
    fontSize: '0.9rem',
  } as React.CSSProperties,

  thRowLabel: {
    padding: '0.75rem 1rem',
    textAlign: 'left' as const,
    fontWeight: 600,
    color: 'var(--text-primary)',
    backgroundColor: 'var(--surface-color)',
    borderRight: '1px solid var(--border-color)',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,

  td: {
    padding: '0.75rem 1rem',
    textAlign: 'center' as const,
    color: 'var(--text-primary)',
    borderBottom: '1px solid var(--border-color)',
  } as React.CSSProperties,

  trEven: {
    backgroundColor: 'var(--background-color)',
  } as React.CSSProperties,

  trOdd: {
    backgroundColor: 'var(--surface-color)',
  } as React.CSSProperties,

  /**
   * Mobile Card Styles
   */
  mobileCard: {
    backgroundColor: 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1rem',
  } as React.CSSProperties,

  mobileCardTierName: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: 'var(--text-primary)',
    margin: '0 0 0.25rem',
  } as React.CSSProperties,

  mobileCardStudents: {
    fontSize: '0.875rem',
    color: 'var(--text-secondary)',
    marginBottom: '1rem',
  } as React.CSSProperties,

  mobileCardRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    borderBottom: '1px solid var(--border-color)',
  } as React.CSSProperties,

  mobileCardLabel: {
    fontSize: '0.875rem',
    color: 'var(--text-secondary)',
  } as React.CSSProperties,

  mobileCardValue: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
  } as React.CSSProperties,

  /**
   * Info Boxes - 3-column grid
   */
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '1.5rem',
    maxWidth: '960px',
    margin: '0 auto',
  } as React.CSSProperties,

  infoGridMobile: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
  } as React.CSSProperties,

  infoBox: {
    backgroundColor: 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    borderRadius: '12px',
    padding: '1.5rem',
    textAlign: 'center' as const,
  } as React.CSSProperties,

  infoBoxIcon: {
    color: '#f38d4b',
    marginBottom: '0.75rem',
  } as React.CSSProperties,

  infoBoxTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: '0.5rem',
  } as React.CSSProperties,

  infoBoxDescription: {
    fontSize: '0.9rem',
    color: 'var(--text-secondary)',
    lineHeight: 1.6,
    margin: 0,
  } as React.CSSProperties,

  /**
   * CTA Section
   */
  cta: {
    textAlign: 'center' as const,
    padding: '2.5rem 0',
  } as React.CSSProperties,

  ctaTitle: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: '1rem',
  } as React.CSSProperties,

  ctaButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.875rem 2rem',
    fontSize: '1rem',
    fontWeight: 600,
    color: 'white',
    backgroundColor: 'var(--primary-color)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textDecoration: 'none',
  } as React.CSSProperties,

  ctaNote: {
    fontSize: '0.875rem',
    color: 'var(--text-secondary)',
    marginTop: '1rem',
  } as React.CSSProperties,
};

// ============================================================================
// CONTACT PAGE STYLES
// ============================================================================

export const portalContactStyles = {
  hero: {
    background: 'linear-gradient(135deg, #00233c 0%, #003a5c 100%)',
    color: 'white',
    padding: '3.5rem 2rem',
    textAlign: 'center' as const,
  } as React.CSSProperties,

  heroTitle: {
    fontSize: '2.5rem',
    fontWeight: 700,
    margin: '0 0 0.75rem',
  } as React.CSSProperties,

  heroTitleMobile: {
    fontSize: '1.75rem',
    fontWeight: 700,
    margin: '0 0 0.75rem',
  } as React.CSSProperties,

  heroSubtitle: {
    fontSize: '1.15rem',
    opacity: 0.9,
    margin: 0,
    lineHeight: 1.6,
  } as React.CSSProperties,

  formCard: {
    maxWidth: '640px',
    margin: '2.5rem auto',
    backgroundColor: 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    borderRadius: '16px',
    padding: '2.5rem',
  } as React.CSSProperties,

  formCardMobile: {
    margin: '1.5rem auto',
    backgroundColor: 'var(--surface-color)',
    border: '1px solid var(--border-color)',
    borderRadius: '12px',
    padding: '1.5rem',
  } as React.CSSProperties,

  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
    marginBottom: '1rem',
  } as React.CSSProperties,

  fieldRowMobile: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
    marginBottom: '1rem',
  } as React.CSSProperties,

  fieldGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.375rem',
  } as React.CSSProperties,

  fullWidthGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.375rem',
    marginBottom: '1rem',
  } as React.CSSProperties,

  label: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
  } as React.CSSProperties,

  input: {
    padding: '0.75rem 1rem',
    fontSize: '0.95rem',
    border: '2px solid var(--border-color)',
    borderRadius: '8px',
    backgroundColor: 'var(--background-color)',
    color: 'var(--text-primary)',
    outline: 'none',
    transition: 'border-color 0.2s',
  } as React.CSSProperties,

  select: {
    padding: '0.75rem 1rem',
    fontSize: '0.95rem',
    border: '2px solid var(--border-color)',
    borderRadius: '8px',
    backgroundColor: 'var(--background-color)',
    color: 'var(--text-primary)',
    outline: 'none',
    transition: 'border-color 0.2s',
    appearance: 'auto' as const,
  } as React.CSSProperties,

  textarea: {
    padding: '0.75rem 1rem',
    fontSize: '0.95rem',
    border: '2px solid var(--border-color)',
    borderRadius: '8px',
    backgroundColor: 'var(--background-color)',
    color: 'var(--text-primary)',
    outline: 'none',
    transition: 'border-color 0.2s',
    resize: 'vertical' as const,
    minHeight: '120px',
    fontFamily: 'inherit',
  } as React.CSSProperties,

  submitButton: {
    width: '100%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '0.875rem 2rem',
    fontSize: '1rem',
    fontWeight: 600,
    color: '#00233c',
    backgroundColor: '#f38d4b',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  } as React.CSSProperties,

  errorText: {
    fontSize: '0.8rem',
    color: 'var(--error-color)',
    margin: 0,
  } as React.CSSProperties,

  honeypot: {
    position: 'absolute' as const,
    left: '-9999px',
    opacity: 0,
    height: 0,
    width: 0,
    overflow: 'hidden' as const,
  } as React.CSSProperties,

  successContainer: {
    textAlign: 'center' as const,
    padding: '2rem 0',
  } as React.CSSProperties,

  successIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 1rem',
  } as React.CSSProperties,

  successTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: '0.5rem',
  } as React.CSSProperties,

  successText: {
    fontSize: '0.95rem',
    color: 'var(--text-secondary)',
    lineHeight: 1.6,
    marginBottom: '1rem',
  } as React.CSSProperties,

  copyButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.625rem 1.25rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--primary-color, #00233c)',
    backgroundColor: 'transparent',
    border: '2px solid var(--border-color)',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  } as React.CSSProperties,

  belowFormText: {
    textAlign: 'center' as const,
    fontSize: '0.9rem',
    color: 'var(--text-secondary)',
    marginBottom: '2rem',
  } as React.CSSProperties,
};
